import { IntegrationCard, PageHeader } from '@fluentsms/agentnet-web-components';
import { createStyles, Grid, makeStyles, Theme } from '@material-ui/core';
import { MarketplaceProduct } from 'api/admin/types';
import { KBLinks } from 'features/constants';
import { useSearchParams } from 'features/help/hooks/useSearchParams';
import { useMessagingQuery } from 'features/help/queries/useMessagingQuery';
import { Message } from 'features/help/types/messagingCollection';
import { useMarketPlace } from 'hooks/useMarketPlace';
import { FC, useEffect, useState } from 'react';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import { IconsMap } from 'ui-kit/icons/MarketplaceIcons';
import { normalizeServiceName } from 'utilities/utilities';
import FeatureDetails from './FeatureDetails';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentWrap: {
      margin: theme.spacing(3),
    },
    verticalSpacing: {
      marginTop: theme.spacing(3),
    },
    cardStyles: {
      height: '260px',
    },
  }),
);

const IntegrationsFeatures: FC = () => {
  const classes = useStyles();
  const [selectedService, setSelectedService] = useState<MarketplaceProduct | null>(null);
  const [allDescriptions, setAllDescriptions] = useState<Record<string, Message | null>>({});
  const searchParams = useSearchParams<{ productCode?: string }>();

  const { isMarketPlaceEnabled, fetchMarketPlaceServices, fetchMarketPlaceServicesResults, fetchMarketPlaceStatus } =
    useMarketPlace();

  useEffect(() => {
    if (isMarketPlaceEnabled) fetchMarketPlaceServices();
  }, [isMarketPlaceEnabled]);

  useEffect(() => {
    let isActive = true;
    if (searchParams?.productCode && fetchMarketPlaceServicesResults) {
      const matchedService = fetchMarketPlaceServicesResults.find(
        (service: MarketplaceProduct) => service.productCode === searchParams.productCode,
      );
      if (isActive && matchedService) {
        setSelectedService(matchedService);
      }
    } else if (selectedService) {
      const updatedService = fetchMarketPlaceServicesResults?.find(
        (service: MarketplaceProduct) => service.productCode === selectedService.productCode,
      );
      updatedService && setSelectedService(updatedService);
    }
    return () => {
      isActive = false;
    };
  }, [fetchMarketPlaceServicesResults, searchParams]);

  useEffect(() => {
    if (fetchMarketPlaceServicesResults) {
      const preload = async () => {
        const slugs = fetchMarketPlaceServicesResults?.map(
          (service) => `feature-${service.productName.toLowerCase().replace(/\s+/g, '-')}`,
        );
        const entries = await Promise.all(slugs?.map((slug) => useMessagingQuery(slug)));
        const result = Object.fromEntries(
          fetchMarketPlaceServicesResults.map((service, idx) => [service.productCode, entries[idx].data]),
        );
        setAllDescriptions(result);
      };

      preload();
    }
  }, [fetchMarketPlaceServicesResults]);

  const ServiceIcon = ({ serviceName }: { serviceName: string }) => {
    const normalizedName = normalizeServiceName(serviceName);
    const IconComponent = IconsMap[normalizedName] || IconsMap['default'];

    return <IconComponent />;
  };

  return (
    <ContentContainer fullWidth>
      <LoadingSpinner status={fetchMarketPlaceStatus} variant="linear" />
      <div className={classes.contentWrap}>
        {!selectedService ? (
          <>
            <PageHeader
              title="Add-Ons"
              subtitle="Enable and manage options for additional services available within your files."
              menuItems={[
                {
                  label: 'Knowledge Base',
                  link: KBLinks.addOns,
                },
              ]}
            />
            <Grid container spacing={1} className={classes.verticalSpacing}>
              {fetchMarketPlaceServicesResults?.map((service: MarketplaceProduct) => (
                <Grid item sm={3} key={service?.productName}>
                  <IntegrationCard
                    testId={`add-ons-card-${normalizeServiceName(service?.productName)}`}
                    title={service?.productName?.includes('Enhanced') ? 'Identity Verification' : service?.productName}
                    icon={<ServiceIcon serviceName={service?.productName} />}
                    description={service?.description}
                    category={service?.category}
                    enabled={service?.isEnabled}
                    onClick={() => setSelectedService(service)}
                    className={classes.cardStyles}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <FeatureDetails
            goBackAction={() => setSelectedService(null)}
            selectedService={selectedService}
            refreshServiceList={fetchMarketPlaceServices}
            ServiceIcon={<ServiceIcon serviceName={selectedService?.productName} />}
            allDescriptions={allDescriptions}
          />
        )}
      </div>
    </ContentContainer>
  );
};

export default IntegrationsFeatures;
